"use client"

import { LinkProps } from "@mui/material"
import { SnackbarMessage, useSnackbar, VariantType } from "notistack"
import { startTransition, useCallback } from "react"

declare module "notistack" {
  interface VariantOverrides {
    // removes the `warning` variant
    default: false
    success: {
      title: string
      options?: {
        action?: string
        actionProps?: LinkProps
      }
    }
    error: {
      title: string
      options?: {
        action?: string
        actionProps?: LinkProps
      }
    }
    info: {
      title: string
      options?: {
        action?: string
        actionProps?: LinkProps
      }
    }
    warning: {
      title: string
      options?: {
        action?: string
        actionProps?: LinkProps
      }
    }
  }
}

export interface Options {
  action?: string
  actionProps?: LinkProps
}

export const useToast = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const push = (
    title: string,
    message: string,
    variant: VariantType,
    persist = false,
    options?: Options,
    key?: string,
  ) => {
    let content: SnackbarMessage = message
    if (key) {
      content = <div id={key}>{message}</div>
    }
    startTransition(() => {
      enqueueSnackbar(content, {
        variant,
        persist,
        title,
        key,
        options,
      })
    })
  }

  const success = useCallback(
    (title: string, message: string, persist?: boolean, options?: Options) =>
      push(title, message, "success", persist, options),
    [],
  )
  const warning = useCallback(
    (
      title: string,
      message: string,
      persist?: boolean,
      options?: Options,
      key?: string,
    ) => push(title, message, "warning", persist, options, key),
    [],
  )
  const info = useCallback(
    (
      title: string,
      message: string,
      persist?: boolean,
      options?: Options,
      key?: string,
    ) => push(title, message, "info", persist, options, key),
    [],
  )
  const error = useCallback(
    (title: string, message: string, persist?: boolean, options?: Options) =>
      push(title, message, "error", persist, options),
    [],
  )
  const dismiss = useCallback(
    (key?: string) => closeSnackbar(key),
    [closeSnackbar],
  )
  return { success, warning, info, error, dismiss }
}
